import { DataImport, Filter, FilterClause } from "@coeff/api";
import { NetSuiteFilter } from "@coeff/utils";
import moment from "moment";
import React from "react";
import styled from "styled-components";

import { COLORS } from "../constants";
import { DATE_FILTER_OPTIONS, ALL_FILTER_OPERATORS } from "../constants/filters";

import { Card } from "./Card";

const StyledPanel = styled.div`
  margin-left: 10px;
`;

const Row = styled.div`
  margin: 8px 0px;
`;
const Bold = styled.span`
  font-weight: bold;
`;

const NestedFilterClauseContainerItem = styled.div<{ depth: number }>`
  background: ${props => (props.depth >= 2 ? COLORS.white : COLORS.black4)};
  padding: ${props => (props.depth === 2 ? "4px 10px" : "0px")};
`;

const NestedFilterClauseOperand = styled.div<{ depth: number }>`
  margin: 10px ${props => (props.depth === 1 ? "10px" : "0px")};
  color: ${COLORS.black45};
  font-weight: bold;
`;

const getColumnFriendlyLabel = (
  columnName: string | undefined,
  labelRef: { [label: string]: string } | undefined
): string => {
  return columnName ? (labelRef && labelRef[columnName]) ?? columnName : "";
};

/** NetSuite CRX report filters use fieldLabel/fieldName/value instead of column_name/operands */
function isNetSuiteFilter(filter: Filter | NetSuiteFilter): filter is NetSuiteFilter {
  return (
    typeof (filter as NetSuiteFilter).fieldLabel !== "undefined" &&
    typeof (filter as NetSuiteFilter).fieldName !== "undefined"
  );
}

function getNetSuiteFilterDisplayValue(filter: NetSuiteFilter): string {
  if (filter.fieldType === "checkbox") {
    return filter.value ? "Yes" : "No";
  }
  if (filter.fieldType === "readonlyText") {
    return "—";
  }
  if ("checkAll" in filter && filter.checkAll && filter.fieldType === "select") {
    return "All";
  }
  const v = "value" in filter ? filter.value : undefined;
  if (v == null || v === "") {
    return "—";
  }
  if (Array.isArray(v)) {
    return v.length > 0 ? v.join(", ") : "All";
  }
  return String(v);
}

const getSingleFilter = (filter: Filter, labelRef: { [label: string]: string }) => {
  let operandDisplay: string | undefined = (filter.operands ?? [])
    .map(
      operand =>
        operand.text ??
        operand.number ??
        operand.boolean?.toString() ??
        operand.cell_ref_a1 ??
        moment(operand.date).format("MM/DD/YYYY")
    )
    .join(" - ");

  if (filter.relative_date_filter_type) {
    const dataFilter = DATE_FILTER_OPTIONS.find(
      df =>
        filter.relative_date_filter_type &&
        df.dateFilterTypes.includes(filter.relative_date_filter_type)
    );
    operandDisplay = (() => {
      switch (dataFilter?.dataKey) {
        case "last":
        case "next":
          return `${dataFilter?.dataKey} ${operandDisplay} ${filter.relative_date_filter_type
            .split("_")
            .pop()}`;
        case "exact_dates":
        case "exact_date":
          return operandDisplay;
        default:
          return dataFilter?.label;
      }
    })();
  }

  if (filter.operator_type === "location_is_exactly") {
    operandDisplay = `${filter.operands?.[0]?.text ?? ""}, ${filter.operands?.[1]?.text ?? ""}`;
  }

  if (filter.operator_type === "location_is_in_the_circle") {
    const [radius, unit, centerLat, centerLng] = filter.operands || [];
    operandDisplay = `${radius?.text ?? ""} ${unit?.text ?? ""} from (${centerLat?.text ?? ""}, ${
      centerLng?.text ?? ""
    })`;
  }

  if (filter.operator_type === "location_is_in_the_box") {
    const [topLeftLat, topLeftLng, bottomRightLat, bottomRightLng] = filter.operands || [];
    operandDisplay = `(${topLeftLat?.text ?? ""}, ${topLeftLng?.text ?? ""}) to (${
      bottomRightLat?.text ?? ""
    }, ${bottomRightLng?.text ?? ""})`;
  }

  return (
    <Row>
      <Bold>{getColumnFriendlyLabel(filter.column_name, labelRef)}</Bold>{" "}
      {ALL_FILTER_OPERATORS.find(op => op.dataKey === filter.operator_type)?.label}{" "}
      <Bold>{operandDisplay}</Bold>
    </Row>
  );
};

const NestedFilterClause = ({
  filterClause,
  labelRef,
  depth = 0,
}: {
  filterClause: FilterClause;
  labelRef: { [label: string]: string };
  depth?: number;
}) => {
  if (!filterClause) {
    return null;
  }

  if (filterClause.filter) {
    return (
      <>
        {getSingleFilter(filterClause.filter, {
          ...labelRef,
          ...(filterClause.filter.column_name && filterClause.filter.column_label
            ? { [filterClause.filter.column_name]: filterClause.filter.column_label }
            : {}),
        })}
      </>
    );
  }

  return (
    <NestedFilterClauseContainerItem depth={depth}>
      {filterClause.filter_clauses?.map((cls, idx) => (
        <div key={idx}>
          {idx > 0 && (
            <NestedFilterClauseOperand depth={depth}>
              {filterClause.clause_operand ?? "AND"}
            </NestedFilterClauseOperand>
          )}
          <NestedFilterClause filterClause={cls} labelRef={labelRef} depth={depth + 1} />
        </div>
      ))}
    </NestedFilterClauseContainerItem>
  );
};

type FiltersAndSortsProps = {
  dataImport?: DataImport;
};

export const FiltersAndSorts = (props: FiltersAndSortsProps) => {
  const columns = props.dataImport?.columns ?? [];
  const filters =
    props.dataImport?.source_info?.ppress_params?.filters ?? props.dataImport?.filters;
  const sorts = props.dataImport?.source_info?.ppress_params?.sorts ?? props.dataImport?.sorts;
  const limit =
    props.dataImport?.data_source_type === "salesforce"
      ? props.dataImport?.source_info?.salesforce_objects_limit
      : props.dataImport?.data_source_type === "hubspot"
      ? props.dataImport?.source_info?.hubspot_objects_limit
      : props.dataImport?.source_info?.sql_limit;

  const labelRef = columns.reduce<{ [name: string]: string }>((obj, col) => {
    if (col.column_name && col.alias) {
      obj[col.column_name] = col.alias;
    }
    return obj;
  }, {});

  const filterClause =
    props.dataImport?.source_info?.ppress_params?.filter_clause || props.dataImport?.filter_clause;

  const isNetSuiteCrxFilters =
    props.dataImport?.data_source_type === "netsuite_crx" &&
    (filters ?? []).length > 0 &&
    isNetSuiteFilter((filters ?? [])[0]);

  return (
    <Card>
      <StyledPanel>
        {filterClause ? (
          <NestedFilterClause filterClause={filterClause} labelRef={labelRef} />
        ) : isNetSuiteCrxFilters ? (
          (filters ?? []).map((filter, i) =>
            isNetSuiteFilter(filter) ? (
              <Row key={i}>
                <Bold>{filter.fieldLabel}</Bold>: {getNetSuiteFilterDisplayValue(filter)}
              </Row>
            ) : null
          )
        ) : (
          (filters ?? []).map((filter, i) => getSingleFilter(filter, labelRef))
        )}

        {!filters ||
          (filters.length === 0 && sorts && sorts.length > 0 && !filterClause && (
            <Row style={{ color: COLORS.black45 }}>No filters</Row>
          ))}

        {(sorts ?? []).map((sort, i) => (
          <Row key={i} className="coeff__sort-summary">
            Sort: <Bold>{getColumnFriendlyLabel(sort.column_name, labelRef)}</Bold> (
            {sort.sort_type === "asc" ? "↑ ASC" : "↓ DESC"})
          </Row>
        ))}

        {!!limit && (
          <Row>
            Limit: <Bold>{limit}</Bold> rows
          </Row>
        )}

        {(!filters || !filters.length) && (!sorts || !sorts.length) && !limit && !filterClause && (
          <Row style={{ color: COLORS.black45 }}>No filters or sorts applied</Row>
        )}
      </StyledPanel>
    </Card>
  );
};
